import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { logger } from "#app/monitoring/Logger";
import { showErrorToast } from "#app/notifications";
import { uploadPartFiles } from "#modules/files/infra/uploadPartFiles.api";
import { createPart, CreatePartRequestBody } from "#modules/parts/infra/createPart.api";
import { PartDto } from "#modules/parts/infra/Part.dto";
import { quoteRequestLinesQuery } from "#modules/quoteRequestLines/quoteRequestLinesQuery";

import { partQuery } from "../infra/part.query";

type Options = {
  onSuccess?: (createdPart: PartDto | null) => void;
};

export const useCreatePart = (options?: Options) => {
  const queryClient = useQueryClient();

  const [progress, setProgress] = useState(0);

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: async ({
      data,
    }: {
      data: Omit<CreatePartRequestBody, "files"> & {
        files: {
          name: string;
          data: File;
          hash: string;
          size?: number;
        }[];
      };
    }) => {
      setProgress(0);
      const files = await uploadPartFiles(data.files, setProgress);

      const payload: CreatePartRequestBody = {
        ...data,
        files,
      };

      const part = await createPart({
        ...payload,
        computeEmbeddings: false,
      });

      return part;
    },

    onSuccess: async (part) => {
      if (!part) {
        return;
      }
      await queryClient.invalidateQueries(quoteRequestLinesQuery());
      await queryClient.invalidateQueries(partQuery(part.id));
      options?.onSuccess?.(part);
    },
    onError: (err) => {
      logger.error(err);

      if (err instanceof Error) {
        showErrorToast({
          title: err.message,
        });
      }
    },
  });

  const resetProgress = () => setProgress(0);

  return {
    createPart: mutate,
    createPartAsync: mutateAsync,
    progress,
    resetProgress,
    ...rest,
  };
};
